import React from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DISPLAY_DATE_FORMAT,
  parseToDayjs,
  toApiDateString,
} from "../../utils/displayDateFormat";

/** Match typical sibling inputs like Fee (modest radius, not pill). */
const FORM_CONTROL_MATCH_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 55, // 42 * 1.3
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
    "& fieldset": {
      borderColor: "#BCBEC0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#BCBEC0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#BCBEC0",
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
    },
  },
  "& .MuiOutlinedInput-input": {
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
    padding: "8px 12px",
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#adb5bd",
    opacity: 1,
  },
  // MUI X sectioned date field empty placeholders
  "& .MuiPickersSectionList-sectionContent[data-placeholder]": {
    color: "#adb5bd",
  },
  "& .MuiPickersInputBase-sectionContent[data-placeholder]": {
    color: "#adb5bd",
  },
  "& .MuiInputAdornment-root": {
    marginRight: "4px",
  },
  "& .MuiInputAdornment-root .MuiIconButton-root": {
    color: "#6c757d",
  },
};

/**
 * Date picker that shows DD/MM/YYYY but keeps value/onChange as YYYY-MM-DD strings
 * (same contract as native <input type="date">).
 * When className includes "form-control", styles align with sibling bootstrap inputs.
 */
const ApiDatePickerField = ({
  id,
  value = "",
  onChange,
  disabled = false,
  maxDate,
  minDate,
  className,
  sx,
  slotProps,
  ...rest
}) => {
  const dayjsValue = parseToDayjs(value);
  const effectiveClassName =
    className || slotProps?.textField?.className || "";
  const matchFormControl = String(effectiveClassName)
    .split(/\s+/)
    .includes("form-control");

  const textFieldSx = {
    ...(matchFormControl ? FORM_CONTROL_MATCH_SX : {}),
    ...sx,
    ...slotProps?.textField?.sx,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        format={DISPLAY_DATE_FORMAT}
        value={dayjsValue}
        disabled={disabled}
        maxDate={maxDate ? parseToDayjs(maxDate) || undefined : undefined}
        minDate={minDate ? parseToDayjs(minDate) || undefined : undefined}
        onChange={(next) => {
          if (!onChange) return;
          if (!next || !dayjs(next).isValid()) {
            onChange("");
            return;
          }
          onChange(toApiDateString(next));
        }}
        slotProps={{
          ...slotProps,
          textField: {
            id,
            size: matchFormControl ? "medium" : "small",
            fullWidth: true,
            className: effectiveClassName || undefined,
            ...slotProps?.textField,
            sx: textFieldSx,
          },
        }}
        sx={sx}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export default ApiDatePickerField;
